<template>
  <net-popup-menu>
    <template #title>{{ $t("objectNames.momentum") }}</template>

    <input-text-editor
      :label="$t('labels.description')"
      :value="ctx.momentum.description"
      @update:value="ctx.updateKey('description', $event)"
    />

    <div class="column">
      <section>
        <div class="line-docanchor">
          <label class="label">{{ $t("labels.value") }}</label>
          <documentation-anchor :object="ctx.momentum" prop="curve" />
        </div>
        <input-curve
          :value="ctx.element?.value ?? 0"
          :x-min="ctx.element?.minValue ?? null"
          :x-max="ctx.element?.maxValue ?? null"
          :point-list="curvePoints"
          :type="curveType"
          :fixed-start="false"
          :formula="ctx.momentum.formula"
          class="mt-1"
          @change="onGraphChange"
          @update:formula="ctx.updateKey('formula', $event)"
          @hovervalue="updateCurveHover($event)"
        />
        <p>
          {{ $t("labels.xAxis") }}{{ $t(":") }}
          {{ $t("labels.valueOfX", [ctx.element?.title || $t("labels.unnamed")])
          }}<br />
          {{ $t("labels.yAxis") }}{{ $t(":") }}
          {{
            $t("labels.influenceOnX", [ctx.element?.title || $t("labels.unnamed")])
          }}
        </p>
        <i18n-t
          keypath="texts.influenceCurveConstantExample"
          tag="p"
          scope="global"
        >
          <template #startElement>
            <em>{{ ctx.element?.title || $t("labels.unnamed") }}</em>
          </template>
          <template v-if="ctx.element?.unit" #startUnit
            > {{ ctx.element?.unit }}
          </template>
          <template #endElement>
            <em>{{ ctx.element?.title || $t("labels.unnamed") }}</em>
          </template>
          <template #startValue>{{ curveHover.x }}</template>
          <template #impulseValue>
            {{ formatNumber(curveHover.y || 0, "displaySign") }}
          </template>
          <template v-if="ctx.element?.unit" #endUnit
            > {{ ctx.element?.unit }}
          </template>
        </i18n-t>
      </section>
    </div>

    <div>
      <div class="line-docanchor">
        <label class="label mt-0">
          {{ $t("labels.waitTimeBetweenRounds") }}{{ $t(":") }}
        </label>
        <documentation-anchor :object="ctx.momentum" prop="interval" />
      </div>
      <input-number
        :value="ctx.momentum.interval"
        :min="1"
        @update:value="ctx.updateKey('interval', $event)"
      />
    </div>

    <input-image
      :id="ctx.momentum.imageId || undefined"
      :max-width="1000"
      :max-height="1000"
      :compression="0.9"
      :control-object-fit="true"
      @update="updateImage"
      @delete="deleteImage"
    />

    <button
      class="button button_secondary mv-2"
      type="button"
      @click="ctx.delete()"
    >
      {{ $t("actions.deleteMomentum", 1) }}
    </button>
  </net-popup-menu>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { formatNumber } from "@/helpers/utility/Formatting";

import NetPopupMenu from '@editor/net/NetPopupMenu.vue';
import InputTextEditor from '@core/input/TextEditor.vue';
import InputNumber from "@core/input/InputNumber.vue";
import InputImage from "@editor/items/image/InputImage.vue";
import InputCurve from "@editor/items/relation/InputCurve.vue";
import DocumentationAnchor from "@editor/ui/DocumentationAnchor.vue";

import { useMomentum } from "./useMomentum";
import { useMomentumStore } from "../momentum/momentums.store";

import type { MomentumItem } from '../momentum/MomentumItem';
import type { StakeholderItem } from "../stakeholder/StakeholderItem";
import type { GraphPoint } from "../relation/GraphPoint";
import type { CurveFormType } from "../relation/RelationItem";

const props = defineProps<{
  momentumId: number;
}>();

defineEmits<{
  (e: "select", item: MomentumItem | StakeholderItem): void;
}>();

// const features = useFeatures();
const momentumStore = useMomentumStore();

const ctx = useMomentum(() => props.momentumId);

// ======================
// CURVE

const curvePoints = computed(() => ctx.momentum.curvePoints);
const curveType = computed(() => ctx.momentum.curveType);

const curveHover = reactive({
  x: 0,
  y: 0,
  x2: 0,
  y2: 0
});

function updateCurveHover(hoverData: {
  x: number;
  y: number;
  x2: number;
  y2: number;
}) {
  curveHover.x = hoverData.x;
  curveHover.y = hoverData.y;
  curveHover.x2 = hoverData.x2;
  curveHover.y2 = hoverData.y2;
}

function onGraphChange(data: { points: GraphPoint[]; type: CurveFormType }) {
  momentumStore.updateCurve({
    id: props.momentumId,
    curvePoints: data.points,
    curveType: data.type
  });
}

// ======================
// IMAGE

function updateImage(image: { id: number }) {
  return ctx.updateKey("imageId", image.id);
}

function deleteImage() {
  return ctx.updateKey("imageId", null);
}
</script>